
import { buildOgLocaleMeta, buildOgImageMeta } from '@/utils/seo'
import { H7_COMPARISON_ARTICLE } from '@/utils/staticBlogArticles'
import {
  H7_POWERFUL_LED_PRODUCTS,
  H7_COMPACT_LED_PRODUCTS,
  H7_ALL_PRODUCT_IDS,
  loadH7ComparisonProducts,
  buildH7ProductRow,
} from '~/utils/h7ComparisonProducts'
import {
  H7_CATEGORY_ID,
  buildH7CategoryRoute,
  isH7CategoryRestricted,
} from '~/utils/h7Category'

export default {
  data() {
    return {
      currentYear: new Date().getFullYear(),
      article: H7_COMPARISON_ARTICLE,
      h7Category: null,
      breadcrumb_list: [
        {
          text: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.breadcrumbHome'
          ),
          to: { name: 'index' },
        },
        {
          text: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.breadcrumbBlog'
          ),
          to: {
            name: 'blog-led-accessoires-vehicules',
          },
        },
        {
          text: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.breadcrumbComparative'
          ),
        },
      ],
      chartInstance: null,
      chartReady: false,
      products: {},
    }
  },

  async fetch() {
    const [products, category] = await Promise.all([
      loadH7ComparisonProducts(
        this.$productService,
        `${this.$i18n.locale}:${this.$store.state.country}`
      ),
      this.$categoryService
        .fetchCategoryPage(H7_CATEGORY_ID)
        .then(({ data }) => data)
        .catch(() => null),
    ])

    this.products = products
    this.h7Category = category
  },

  head() {
    return {
      title: this.$t(
        'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaTitle',
        { year: this.currentYear }
      ),
      link: [
        {
          rel: 'preload',
          as: 'image',
          fetchpriority: 'high',
          href: '/ampoule-led-h7-voiture.jpg',
        },
        {
          rel: 'canonical',
          hid: 'canonical',
          href: this.canonicalUrl,
        },
      ],
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaDescription',
            { year: this.currentYear }
          ),
        },
        { hid: 'og:type', property: 'og:type', content: 'article' },
        {
          hid: 'og:url',
          property: 'og:url',
          content: this.canonicalUrl,
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaTitle',
            { year: this.currentYear }
          ),
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaDescription',
            { year: this.currentYear }
          ),
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'AGM Vision',
        },
        ...buildOgLocaleMeta(this.$i18n.locale),
        ...buildOgImageMeta({
          url: `${this.$config.url}/ampoule-led-h7-voiture.jpg`,
          width: 1000,
          height: 700,
          alt: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaTitle',
            { year: this.currentYear }
          ),
        }),
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaTitle',
            { year: this.currentYear }
          ),
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: this.$t(
            'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.metaDescription',
            { year: this.currentYear }
          ),
        },
      ],
      script: [
        {
          src: 'https://cdn.jsdelivr.net/npm/chart.js',
          body: true,
        },
      ],
    }
  },

  computed: {
    canonicalUrl() {
      return (
        this.$config.url +
        this.localePath({
          name: 'blog-led-accessoires-vehicules-comparatif-meilleures-ampoules-led-h7',
        })
      )
    },
    productList() {
      return H7_ALL_PRODUCT_IDS.map(id => this.products[id]).filter(Boolean)
    },

    categoryUrl() {
      if (
        !this.h7Category ||
        isH7CategoryRestricted(this.h7Category, this.$i18n.locale)
      ) {
        return null
      }

      const route = buildH7CategoryRoute(this.h7Category)

      return route ? this.localePath(route) : null
    },

    powerfulLedData() {
      return H7_POWERFUL_LED_PRODUCTS.map(config =>
        buildH7ProductRow(this.products[config.productId], config, route =>
          this.localePath(route)
        )
      ).filter(Boolean)
    },

    compactLedData() {
      return H7_COMPACT_LED_PRODUCTS.map(config =>
        buildH7ProductRow(this.products[config.productId], config, route =>
          this.localePath(route)
        )
      ).filter(Boolean)
    },

    productsData() {
      return [...this.powerfulLedData, ...this.compactLedData]
    },

    chartTableData() {
      return [...this.productsData].sort(
        (a, b) => b.gainNumeric - a.gainNumeric
      )
    },

    // Produit le plus puissant (gain de luminosité le plus élevé)
    mostPowerfulProduct() {
      if (!this.productsData || this.productsData.length === 0) return null
      return this.productsData.reduce((max, product) =>
        product.gainNumeric > max.gainNumeric ? product : max
      )
    },

    // Meilleur anti-erreur (niveau le plus élevé)
    bestAntiErrorProduct() {
      if (!this.productsData || this.productsData.length === 0) return null
      return this.productsData.reduce((best, product) => {
        if (!product.errorLevel) return best
        if (!best.errorLevel) return product
        return product.errorLevel > best.errorLevel ? product : best
      })
    },

    // Plus de luminosité en lumens (extrait des données lumens)
    mostBrightnessProduct() {
      if (!this.productsData || this.productsData.length === 0) return null
      return this.productsData.reduce((max, product) => {
        const currentLumens = this.extractLumens(product.lumens)
        const maxLumens = this.extractLumens(max.lumens)
        return currentLumens > maxLumens ? product : max
      })
    },

    // Meilleur compact (meilleur gain parmi les produits compacts)
    bestCompactProduct() {
      if (!this.productsData || this.productsData.length === 0) return null
      const compactIds = H7_COMPACT_LED_PRODUCTS.map(c => c.productId)
      const compactProducts = this.productsData.filter(p =>
        compactIds.includes(p.productId)
      )
      if (compactProducts.length === 0) return null
      return compactProducts.reduce((max, product) =>
        product.gainNumeric > max.gainNumeric ? product : max
      )
    },

    // Meilleur rapport qualité/prix (meilleur gain/prix parmi les LED puissantes)
    bestValueProduct() {
      if (!this.productsData || this.productsData.length === 0) return null
      const compactIds = H7_COMPACT_LED_PRODUCTS.map(c => c.productId)
      const powerfulProducts = this.productsData.filter(
        p => !compactIds.includes(p.productId)
      )
      if (powerfulProducts.length === 0) return null
      return powerfulProducts.reduce((best, product) => {
        const currentValue =
          product.gainNumeric / this.extractPrice(product.price)
        const bestValue = best.gainNumeric / this.extractPrice(best.price)
        return currentValue > bestValue ? product : best
      })
    },
  },

  watch: {
    productsData(value) {
      if (value.length > 0 && !this.chartReady) {
        this.waitForChartJs()
      }
    },
  },

  async mounted() {
    if (this.$fetchState.error) {
      this.$fetch()
    }
    // Attendre que Chart.js soit chargé avant d'initialiser le graphique
    this.waitForChartJs()
  },

  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },

  methods: {
    waitForChartJs() {
      // Vérifier si Chart.js est déjà chargé
      if (typeof window.Chart !== 'undefined') {
        this.$nextTick(() => {
          this.initChart()
        })
        return
      }

      // Sinon, attendre que le script soit chargé
      const checkInterval = setInterval(() => {
        if (typeof window.Chart !== 'undefined') {
          clearInterval(checkInterval)
          this.$nextTick(() => {
            this.initChart()
          })
        }
      }, 100) // Vérifier toutes les 100ms

      // Timeout de sécurité après 5 secondes
      setTimeout(() => {
        clearInterval(checkInterval)
      }, 5000)
    },

    initChart() {
      if (!this.$refs.brightnessChart || typeof window.Chart === 'undefined') {
        return
      }

      // Utiliser uniquement les données réelles des produits
      if (!this.productsData || this.productsData.length === 0) {
        return
      }

      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const allLedData = this.productsData.map(item => ({
        model: item.model,
        gainNumeric: item.gainNumeric,
      }))

      allLedData.sort((a, b) => b.gainNumeric - a.gainNumeric)

      const chartLabels = allLedData.map(item => item.model)
      const chartDataPoints = allLedData.map(item => item.gainNumeric)

      const ctx = this.$refs.brightnessChart.getContext('2d')

      const isMobile = window.innerWidth < 768

      this.chartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: chartLabels,
          datasets: [
            {
              label: this.$t(
                'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.chart.label'
              ),
              data: chartDataPoints,
              backgroundColor: chartDataPoints.map(gain => {
                if (gain >= 700) return 'rgba(34, 197, 94, 0.7)'
                if (gain >= 500) return 'rgba(59, 130, 246, 0.7)'
                if (gain >= 400) return 'rgba(139, 92, 246, 0.7)'
                if (gain >= 200) return 'rgba(234, 179, 8, 0.7)'
                return 'rgba(249, 115, 22, 0.7)'
              }),
              borderColor: chartDataPoints.map(gain => {
                if (gain >= 700) return 'rgba(22, 163, 74, 1)'
                if (gain >= 500) return 'rgba(37, 99, 235, 1)'
                if (gain >= 400) return 'rgba(124, 58, 237, 1)'
                if (gain >= 200) return 'rgba(202, 138, 4, 1)'
                return 'rgba(234, 88, 12, 1)'
              }),
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: !isMobile,
                text: this.$t(
                  'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.chart.xAxisTitle'
                ),
              },
              ticks: {
                font: {
                  size: isMobile ? 10 : 12,
                },
              },
            },
            y: {
              ticks: {
                autoSkip: false,
                font: {
                  size: isMobile ? 8 : 10,
                },
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label(context) {
                  let label = context.dataset.label || ''
                  if (label) {
                    label += ': '
                  }
                  if (context.parsed.x !== null) {
                    label += context.parsed.x + '%'
                  }
                  return label
                },
              },
            },
          },
        },
      })

      this.chartReady = true
    },

    // Extraire les lumens d'une chaîne comme "5200 lm / 50 W"
    extractLumens(lumensString) {
      if (!lumensString) return 0
      const match = lumensString.match(/(\d+[\s,]?\d*)\s*lm/)
      return match ? parseInt(match[1].replace(/[\s,]/g, ''), 10) : 0
    },

    // Extraire le prix d'une chaîne formatée comme "99,90 €"
    extractPrice(priceString) {
      if (!priceString) return Infinity
      const match = priceString.match(/(\d+[,.]?\d*)/)
      return match ? parseFloat(match[1].replace(',', '.')) : Infinity
    },

    // Formater le niveau d'anti-erreur avec traduction
    formatErrorLevel(level) {
      if (level === null || level === undefined) {
        return this.$t(
          'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.comparison.noAntiError'
        )
      }
      const stars = '✴'.repeat(level)
      return `${stars} ${this.$t(
        'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.comparison.level'
      )} ${level}`
    },
  },
}
